import { useState, useEffect, useMemo } from "react";
import { AppointmentData, fetchAppointmentById } from "@/services/appointment-service";
import { AgendaSlot, SlotAppointment, FitInAppointment, fetchSlotAgenda } from "@/services/shift-reassign-service";
import { fetchDentists, Dentist } from "@/services/dentist-service";
import { useClinic } from "@/contexts/ClinicContext";

import { Check } from "lucide-react";
import { EditAppointmentDialog } from "@/components/appointments/EditAppointmentDialog";
import { FitInAppointmentDialog } from "@/components/appointments/FitInAppointmentDialog";
import { ChevronLeft, ChevronRight, RefreshCw, Plus, Minus, PlusSquare, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScheduleStatusColors, statusLabels } from "@/utils/appointmentStatus";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, startOfWeek, addWeeks, subWeeks } from "date-fns";
import { ptBR } from "date-fns/locale";
import { firstAndLastName } from "@/utils/formatters";
import { ShiftReassignDialog } from "@/components/schedule/ShiftReassignDialog";

const DAILY_COLUMN_WIDTH = "330px";
const WEEKLY_COLUMN_WIDTH = "200px";

const TIME_SLOTS = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30"
];

// Paleta Clinical Warmth — tons terrosos dessaturados que harmonizam com o bone bg.
// Cada dentista ganha uma cor com baixa saturação mas identidade forte.
const DENTIST_COLORS = [
  { bg: "bg-[hsl(158,28%,88%)]", border: "border-[hsl(158,34%,55%)]", text: "text-[hsl(158,40%,20%)]" }, // sage
  { bg: "bg-[hsl(32,45%,90%)]",  border: "border-[hsl(32,55%,58%)]",  text: "text-[hsl(28,50%,22%)]"  }, // ochre
  { bg: "bg-[hsl(12,55%,92%)]",  border: "border-[hsl(12,60%,65%)]",  text: "text-[hsl(8,55%,28%)]"   }, // clay coral
  { bg: "bg-[hsl(220,30%,92%)]", border: "border-[hsl(220,35%,60%)]", text: "text-[hsl(220,40%,25%)]" }, // slate blue
  { bg: "bg-[hsl(280,22%,92%)]", border: "border-[hsl(280,28%,62%)]", text: "text-[hsl(280,35%,25%)]" }, // plum
  { bg: "bg-[hsl(180,25%,89%)]", border: "border-[hsl(180,32%,50%)]", text: "text-[hsl(180,42%,18%)]" }, // teal dust
];

const OPTIONAL_SLOTS = ["13:00", "13:30"];

const Schedule = () => {
  const { selectedClinicId, selectedClinic } = useClinic();

  const [agendaSlots, setAgendaSlots] = useState<AgendaSlot[]>([]);
  const [fitIns, setFitIns] = useState<FitInAppointment[]>([]);
  const [dentists, setDentists] = useState<Dentist[]>([]);
  const [selectedDentists, setSelectedDentists] = useState<string[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fitInDialogOpen, setFitInDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const { toast } = useToast();

  const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isShiftReassignOpen, setIsShiftReassignOpen] = useState(false);

  useEffect(() => {
    fetchDentists(false, false, "", 1, 100)
      .then(setDentists)
      .catch(() => toast({ title: "Erro ao carregar profissionais", variant: "destructive" }));
  }, []);

  useEffect(() => {
    if (selectedClinicId) loadAgenda();
  }, [selectedClinicId, selectedDentists, currentDate, selectedDay, viewMode]);

  const loadAgenda = async () => {
    if (!selectedClinicId) return;
    try {
      setLoading(true);
      const startDate = viewMode === 'day' && selectedDay
        ? format(selectedDay, "yyyy-MM-dd")
        : format(startOfWeek(currentDate, { weekStartsOn: 1 }), "yyyy-MM-dd");
      const endDate = viewMode === 'day' && selectedDay
        ? format(selectedDay, "yyyy-MM-dd")
        : format(addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), 6), "yyyy-MM-dd");

      const response = await fetchSlotAgenda(selectedClinicId, startDate, endDate);

      const isLegacyFormat = Array.isArray(response);
      const slots: AgendaSlot[] = isLegacyFormat ? response : (response.slots || []);
      const rawFitIns: FitInAppointment[] = isLegacyFormat ? [] : (response.fit_ins || []);

      const filteredSlots = selectedDentists.length > 0
        ? slots.filter(s => s.dentist_id && selectedDentists.includes(s.dentist_id))
        : slots;
      setAgendaSlots(filteredSlots);

      const filteredFitIns = selectedDentists.length > 0
        ? rawFitIns.filter(f => f.dentist_id && selectedDentists.includes(f.dentist_id))
        : rawFitIns;
      setFitIns(filteredFitIns);
    } catch {
      toast({ title: "Erro ao carregar agenda", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDentistToggle = (dentistId: string) => {
    setSelectedDentists(prev =>
      prev.includes(dentistId) ? prev.filter(id => id !== dentistId) : [...prev, dentistId]
    );
  };

  const handleEditAppointment = async (appointmentId: string) => {
    try {
      const full = await fetchAppointmentById(appointmentId);
      setSelectedAppointment(full);
      setDialogOpen(true);
    } catch {
      toast({ title: "Erro ao carregar agendamento", variant: "destructive" });
    }
  };

  const handleSave = (_updatedAppointment: AppointmentData) => {
    loadAgenda();
  };

  const navigatePrevious = () => {
    if (viewMode === 'week') setCurrentDate(prev => subWeeks(prev, 1));
    else if (viewMode === 'day' && selectedDay) setSelectedDay(addDays(selectedDay, -1));
  };

  const navigateNext = () => {
    if (viewMode === 'week') setCurrentDate(prev => addWeeks(prev, 1));
    else if (viewMode === 'day' && selectedDay) setSelectedDay(addDays(selectedDay, 1));
  };

  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const getDentistColor = (dentistId: string | null) => {
    if (!dentistId) return { bg: "bg-gray-100", border: "border-gray-300", text: "text-gray-900" };
    const index = dentists.findIndex(d => d.id === dentistId);
    if (index === -1) return { bg: "bg-gray-100", border: "border-gray-300", text: "text-gray-900" };
    return DENTIST_COLORS[index % DENTIST_COLORS.length];
  };

  // Busca fit-ins para uma celula especifica
  const getFitInsForCell = (date: Date, time: string, dentistId?: string) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return fitIns.filter(f =>
      f.slot_date === dateStr &&
      f.slot_time === time &&
      (!dentistId || f.dentist_id === dentistId)
    );
  };

  const renderFitInButton = (fitIn: FitInAppointment, useStatusColors: boolean) => {
    const colors = useStatusColors
      ? { bg: "", border: "", text: "" }
      : getDentistColor(fitIn.dentist_id);
    const statusClass = useStatusColors
      ? ScheduleStatusColors[fitIn.appointment_status as keyof typeof ScheduleStatusColors] || ""
      : `${colors.bg} ${colors.border} ${colors.text}`;

    return (
      <button
        key={fitIn.appointment_id}
        onClick={() => handleEditAppointment(fitIn.appointment_id)}
        className={`schedule-appt-card schedule-fitin ${statusClass} mb-0.5`}
      >
        <div className="font-medium tracking-tight flex items-center gap-1 overflow-hidden"
             title={`${fitIn.patient_name} (encaixe)`}>
          {fitIn.confirmed && (
            <span className="inline-flex items-center justify-center h-3 w-3 shrink-0 rounded-full bg-[hsl(var(--accent))] text-white">
              <Check className="h-2 w-2" strokeWidth={3} />
            </span>
          )}
          <span className="truncate whitespace-nowrap min-w-0">
            {fitIn.patient_name}
            {useStatusColors && ` (${statusLabels[fitIn.appointment_status as keyof typeof statusLabels] || fitIn.appointment_status})`}
          </span>
          <span className="ml-auto text-[10px] font-display font-semibold uppercase tracking-wider shrink-0 text-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.12)] rounded px-1 py-[1px]">enc</span>
        </div>
      </button>
    );
  };

  // Busca slots para uma celula especifica (date + time + dentistId opcional)
  const getSlotsForCell = (date: Date, time: string, dentistId?: string) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return agendaSlots.filter(s =>
      s.slot_date === dateStr &&
      s.slot_time === time &&
      (!dentistId || s.dentist_id === dentistId)
    );
  };

  // Na visao diaria, colunas derivadas dos slots (nao dos appointments)
  const dentistSpecialtyColumns = useMemo(() => {
    if (!selectedDay) return [];
    const dateStr = format(selectedDay, "yyyy-MM-dd");
    const daySlotsAll = agendaSlots.filter(s => s.slot_date === dateStr);

    const seen = new Set<string>();
    const columns: { dentist_id: string; dentist_name: string; dental_specialty_name: string | null }[] = [];

    for (const slot of daySlotsAll) {
      if (!slot.dentist_id) continue;
      const key = `${slot.dentist_id}::${slot.dental_specialty_name || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      columns.push({
        dentist_id: slot.dentist_id,
        dentist_name: slot.dentist_name,
        dental_specialty_name: slot.dental_specialty_name || null,
      });
    }

    columns.sort((a, b) => {
      const nameCompare = a.dentist_name.localeCompare(b.dentist_name, 'pt-BR');
      if (nameCompare !== 0) return nameCompare;
      return (a.dental_specialty_name || '').localeCompare(b.dental_specialty_name || '', 'pt-BR');
    });

    return columns;
  }, [agendaSlots, selectedDay]);

  // Horarios visiveis — opcionais so aparecem se tiverem slots com appointments
  const visibleSlots = useMemo(() => {
    return TIME_SLOTS.filter(time => {
      if (!OPTIONAL_SLOTS.includes(time)) return true;
      return agendaSlots.some(s => s.slot_time === time && s.appointments.length > 0)
        || fitIns.some(f => f.slot_time === time);
    });
  }, [agendaSlots, fitIns]);

  const renderAppointmentButton = (
    apt: SlotAppointment,
    slot: AgendaSlot,
    useStatusColors: boolean
  ) => {
    const colors = useStatusColors
      ? { bg: "", border: "", text: "" }
      : getDentistColor(slot.dentist_id);
    const statusClass = useStatusColors
      ? ScheduleStatusColors[apt.appointment_status as keyof typeof ScheduleStatusColors] || ""
      : `${colors.bg} ${colors.border} ${colors.text}`;

    return (
      <button
        key={apt.appointment_id}
        onClick={() => handleEditAppointment(apt.appointment_id)}
        className={`schedule-appt-card ${statusClass} mb-0.5`}
      >
        <div className="font-medium tracking-tight flex items-center gap-1 overflow-hidden"
             title={apt.patient_name}>
          {slot.block_type && (
            <span className="shrink-0 text-[8px] font-bold uppercase text-muted-foreground bg-muted px-1 rounded">Bloq.</span>
          )}
          {apt.confirmed && (
            <span className="inline-flex items-center justify-center h-3 w-3 shrink-0 rounded-full bg-[hsl(var(--success))] text-white">
              <Check className="h-2 w-2" strokeWidth={3} />
            </span>
          )}
          <span className="truncate whitespace-nowrap min-w-0">
            {apt.patient_name}
            {useStatusColors && ` (${statusLabels[apt.appointment_status as keyof typeof statusLabels] || apt.appointment_status})`}
          </span>
          {(apt.duration_minutes ?? 30) > 30 && (
            <span className="ml-auto text-[10px] font-display font-semibold shrink-0 bg-foreground/10 rounded px-1">1h</span>
          )}
        </div>
      </button>
    );
  };

  const renderBlockedSlot = (slot: AgendaSlot) => (
    <div key={slot.slot_id}
         className="w-full px-1.5 py-1 rounded-md text-[10px] border bg-muted border-border text-muted-foreground font-display italic tracking-tight"
         style={{
           backgroundImage: 'repeating-linear-gradient(-45deg, transparent 0 6px, hsl(var(--foreground)/0.04) 6px 7px)'
         }}>
      {slot.block_type === 'BLOCKED' ? 'Bloqueado' : 'Bloq. externo'}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Painel lateral colapsavel */}
      <div className="flex">
        {isSidebarOpen && (
          <div className="bg-card border-r border-border p-3 space-y-4 overflow-y-auto" style={{ width: 300 }}>
            <div>
              <div className="font-display text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">Calendário</div>
              <Calendar
                mode="single"
                selected={currentDate}
                onSelect={(date) => {
                  if (!date) return;
                  if (viewMode === 'day') setSelectedDay(date);
                  setCurrentDate(date);
                }}
                locale={ptBR}
                weekStartsOn={1}
                className="rounded-md"
              />
            </div>
            <div className="space-y-2 pt-2 border-t border-border/60">
              <div className="flex items-center justify-between">
                <Label className="font-display text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Profissionais</Label>
                <button onClick={() => setSelectedDentists([])} className="text-[10px] italic text-muted-foreground hover:text-accent transition-colors">
                  limpar
                </button>
              </div>
              <div className="space-y-1">
                {dentists.map((dentist) => {
                  const colors = getDentistColor(dentist.id);
                  const isActive = selectedDentists.length === 0 || selectedDentists.includes(dentist.id);
                  const initials = dentist.name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
                  return (
                    <label
                      htmlFor={`dentist-${dentist.id}`}
                      key={dentist.id}
                      className={`flex items-center gap-2 p-1.5 rounded-md cursor-pointer transition-all ${isActive ? 'bg-muted/50' : 'opacity-50 hover:opacity-80'}`}
                    >
                      <Checkbox
                        id={`dentist-${dentist.id}`}
                        checked={isActive}
                        onCheckedChange={() => handleDentistToggle(dentist.id)}
                        className="h-3.5 w-3.5"
                      />
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full border ${colors.bg} ${colors.border} ${colors.text} font-display text-[10px] font-semibold shrink-0`}>
                        {initials}
                      </div>
                      <span className="text-xs flex-1 leading-tight tracking-tight">
                        {dentist.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <div
          className="flex flex-col justify-start items-center bg-card border-r border-border p-1 cursor-pointer"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          style={{ width: 32, userSelect: "none" }}
          title={isSidebarOpen ? "Fechar painel lateral" : "Abrir painel lateral"}
        >
          {isSidebarOpen ? <Minus className="h-5 w-5 text-muted-foreground" /> : <Plus className="h-5 w-5 text-muted-foreground" />}
        </div>
      </div>

      {/* Painel principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Barra superior — vidro sutil + tipografia editorial */}
        <div className="schedule-topbar px-4 py-2 flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={navigatePrevious} className="h-7 w-7">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <div className="font-display text-base font-medium tracking-tight min-w-[220px] tabular-nums">
            {viewMode === 'week'
              ? `${format(weekDays[0], "dd MMM", { locale: ptBR })} — ${format(weekDays[6], "dd MMM yyyy", { locale: ptBR })}`
              : selectedDay ? format(selectedDay, "EEEE, dd 'de' MMMM", { locale: ptBR }) : ""}
          </div>
          <Button variant="outline" size="icon" onClick={navigateNext} className="h-7 w-7">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button variant="outline" size="icon" onClick={loadAgenda} className="h-7 w-7">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setFitInDialogOpen(true)} title="Cadastrar Encaixe" className="h-7 w-7">
            <PlusSquare className="h-3.5 w-3.5" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => setIsShiftReassignOpen(true)} title="Substituicao de Turno" className="h-7 w-7">
            <ArrowLeftRight className="h-3.5 w-3.5" />
          </Button>

          {selectedClinic && (
            <span className="ml-auto font-display text-xs italic text-muted-foreground border-l border-border pl-3">
              {selectedClinic.trade_name}
            </span>
          )}

          {viewMode === 'day' && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setViewMode('week')}>
              Voltar para semana
            </Button>
          )}
        </div>

        {/* Grade */}
        <div className="flex-1 overflow-auto">
          {!selectedClinicId ? (
            <div className="h-full flex items-center justify-center">
              <p className="font-display italic text-lg text-muted-foreground max-w-xs text-center">
                Selecione uma clínica no topo da tela para visualizar a agenda
              </p>
            </div>
          ) : loading ? (
            <div className="h-full flex items-center justify-center gap-2 text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="font-display italic">carregando agenda…</span>
            </div>
          ) : viewMode === 'week' ? (
            <div className="min-w-max">
              <div className="grid sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border"
                   style={{ gridTemplateColumns: `70px repeat(7, ${WEEKLY_COLUMN_WIDTH})` }}>
                <div className="px-2 py-2 border-r border-border bg-muted/60 font-display text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Horário</div>
                {weekDays.map((day) => {
                  const isToday = format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
                  return (
                    <div key={format(day, "yyyy-MM-dd")}
                         className={`px-2 py-2 border-r border-border cursor-pointer text-center transition-colors
                           ${isToday ? "schedule-today" : "bg-muted/40 hover:bg-muted/70"}`}
                         onClick={() => { setViewMode('day'); setSelectedDay(day); }}
                         style={{ maxWidth: WEEKLY_COLUMN_WIDTH, minWidth: WEEKLY_COLUMN_WIDTH }}>
                      <div className="schedule-day-head text-base leading-none tabular-nums">
                        {format(day, "dd", { locale: ptBR })}
                      </div>
                      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground font-sans">
                        {format(day, "EEE", { locale: ptBR }).replace('.', '')}
                      </div>
                    </div>
                  );
                })}
              </div>
              {visibleSlots.map((time, rowIdx) => (
                <div key={time} className="schedule-row grid border-b border-border/70 hover:bg-muted/20 transition-colors"
                     style={{ gridTemplateColumns: `70px repeat(7, ${WEEKLY_COLUMN_WIDTH})`, animationDelay: `${rowIdx * 18}ms` }}>
                  <div className="px-2 py-1.5 border-r border-border/60 bg-muted/25 schedule-time-cell" style={{ minWidth: 70 }}>
                    {time}
                  </div>
                  {weekDays.map((day) => {
                    const cellSlots = getSlotsForCell(day, time);
                    const cellFitIns = getFitInsForCell(day, time);
                    return (
                      <div key={format(day, "yyyy-MM-dd") + '-' + time}
                           className="p-0.5 border-r border-border min-h-[36px] space-y-0.5"
                           style={{ maxWidth: WEEKLY_COLUMN_WIDTH, minWidth: WEEKLY_COLUMN_WIDTH }}>
                        {cellSlots.map((slot) => (
                          <span key={slot.slot_id}>
                            {slot.block_type && slot.appointments.length === 0 && renderBlockedSlot(slot)}
                            {slot.appointments.map(apt => renderAppointmentButton(apt, slot, false))}
                          </span>
                        ))}
                        {cellFitIns.map(f => renderFitInButton(f, false))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : selectedDay && (
            <div className="min-w-max">
              <div className="grid sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border"
                   style={{ gridTemplateColumns: `70px repeat(${dentistSpecialtyColumns.length}, ${DAILY_COLUMN_WIDTH})` }}>
                <div className="px-2 py-2 border-r border-border bg-muted/60 font-display text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Horário</div>
                {dentistSpecialtyColumns.map((col, idx) => {
                  const colors = getDentistColor(col.dentist_id);
                  return (
                    <div key={col.dentist_id + '::' + (col.dental_specialty_name ?? '') + '::' + idx}
                         className="px-2 py-2 border-r border-border text-center bg-muted/40 relative"
                         style={{ maxWidth: DAILY_COLUMN_WIDTH, minWidth: DAILY_COLUMN_WIDTH }}>
                      <div className={`absolute top-0 left-0 right-0 h-[2px] ${colors.border.replace('border-', 'bg-')}`} />
                      <div className="font-display text-sm font-medium tracking-tight">{firstAndLastName(col.dentist_name)}</div>
                      {col.dental_specialty_name && (
                        <div className="text-[10px] italic text-muted-foreground font-display">{col.dental_specialty_name}</div>
                      )}
                    </div>
                  );
                })}
              </div>
              {visibleSlots.map((time, rowIdx) => (
                <div key={time} className="schedule-row grid border-b border-border/70 hover:bg-muted/20 transition-colors"
                     style={{ gridTemplateColumns: `70px repeat(${dentistSpecialtyColumns.length}, ${DAILY_COLUMN_WIDTH})`, animationDelay: `${rowIdx * 18}ms` }}>
                  <div className="px-2 py-1.5 border-r border-border/60 bg-muted/25 schedule-time-cell" style={{ minWidth: 70 }}>
                    {time}
                  </div>
                  {dentistSpecialtyColumns.map((col, idx) => {
                    const cellSlots = getSlotsForCell(selectedDay, time, col.dentist_id)
                      .filter(s => col.dental_specialty_name === null || s.dental_specialty_name === col.dental_specialty_name);
                    const cellFitIns = getFitInsForCell(selectedDay, time, col.dentist_id)
                      .filter(f => col.dental_specialty_name === null || f.dental_specialty_name === col.dental_specialty_name);
                    return (
                      <div key={col.dentist_id + '::' + (col.dental_specialty_name ?? '') + '::' + idx + '-' + time}
                           className="p-0.5 border-r border-border min-h-[36px] space-y-0.5"
                           style={{ maxWidth: DAILY_COLUMN_WIDTH, minWidth: DAILY_COLUMN_WIDTH }}>
                        {cellSlots.map((slot) => (
                          <span key={slot.slot_id}>
                            {slot.block_type && slot.appointments.length === 0 && renderBlockedSlot(slot)}
                            {slot.appointments.map(apt => renderAppointmentButton(apt, slot, true))}
                          </span>
                        ))}
                        {cellFitIns.map(f => renderFitInButton(f, true))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        <EditAppointmentDialog
          appointment={selectedAppointment}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSave={handleSave}
        />
        <ShiftReassignDialog
          open={isShiftReassignOpen}
          onOpenChange={setIsShiftReassignOpen}
          clinicId={selectedClinicId ?? ""}
          dentists={dentists}
          initialDate={
            viewMode === 'day' && selectedDay
              ? format(selectedDay, "yyyy-MM-dd")
              : format(new Date(), "yyyy-MM-dd")
          }
          onSuccess={loadAgenda}
        />

        <FitInAppointmentDialog
          open={fitInDialogOpen}
          onOpenChange={setFitInDialogOpen}
          onSuccess={loadAgenda}
          initialClinicId={selectedClinicId ?? ""}
          initialDate={viewMode === 'day' && selectedDay ? format(selectedDay, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")}
        />
      </div>
    </div>
  );
};

export default Schedule;
